// ─── Voxist Theme Factory ─────────────────────────────────────────
// Usage: const theme = createVoxistTheme('dark' | 'light');

import { createTheme, responsiveFontSizes, ThemeOptions } from '@mui/material/styles';
import { tokens } from './tokens';

// ─── MUI Theme Augmentation ──────────────────────────────────────
// Extends MUI's Theme and ThemeOptions interfaces so that theme.custom
// is fully typed throughout the app. Also declared in theme.d.ts for
// consumers who only need the types without importing the full theme.

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      gradients: typeof tokens.gradients;
      radius: typeof tokens.radius;
      glow: {
        primary: string;
        primaryStrong: string;
        tts: string;
        error: string;
        success: string;
      };
      glass: {
        background: string;
        border: string;
        blur: string;
      };
      fontFamilyMono: string;
    };
  }
  interface ThemeOptions {
    custom?: Theme['custom'];
  }
}

// ─── Shared Component Overrides ──────────────────────────────────

const getComponentOverrides = (mode: 'light' | 'dark'): ThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: {
      html: {
        scrollBehavior: 'smooth',
        '@media (prefers-reduced-motion: reduce)': {
          scrollBehavior: 'auto',
        },
      },
      body: {
        fontFamily: tokens.typography.fontFamily,
      },
      '.skip-link': {
        position: 'absolute',
        top: '-100%',
        left: '16px',
        zIndex: 9999,
        padding: '8px 16px',
        background: tokens.colors.primary.main,
        color: '#ffffff',
        borderRadius: '0 0 8px 8px',
        textDecoration: 'none',
        fontWeight: 700,
        fontSize: '0.875rem',
        '&:focus': {
          top: 0,
        },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '*': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
        },
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      disableRipple: false,
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.pill,
        textTransform: 'none' as const, // default to sentence-case; uppercase only on CTA variants
        fontWeight: 700,
        letterSpacing: '0.04em',
        fontFamily: tokens.typography.fontFamily,
        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:active': {
          transform: 'scale(0.98)',
        },
      },
      sizeMedium: {
        height: 40,
        padding: '8px 24px',
        fontSize: '0.875rem',
      },
      sizeSmall: {
        height: 32,
        padding: '4px 16px',
        fontSize: '0.8125rem',
      },
      sizeLarge: {
        height: 48,
        padding: '12px 32px',
        fontSize: '1rem',
      },
      containedPrimary: {
        textTransform: 'uppercase' as const, // uppercase only on primary CTAs
        letterSpacing: '0.04em',
        background: tokens.gradients.primary,
        color: '#ffffff',
        '&:hover': {
          background: tokens.gradients.primaryHover,
          boxShadow: '0 0 20px rgba(0, 207, 243, 0.25)',
        },
        '&.Mui-disabled': {
          background: mode === 'dark' ? tokens.colors.neutral[700] : tokens.colors.neutral[300],
          color: tokens.colors.neutral[500],
          opacity: 0.7,
        },
      },
      outlinedPrimary: {
        textTransform: 'uppercase' as const, // uppercase only on outline CTAs
        letterSpacing: '0.04em',
        borderColor: mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText,
        borderWidth: '1.5px',
        color: mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText,
        '&:hover': {
          borderWidth: '1.5px',
          borderColor: mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText,
          backgroundColor: mode === 'dark'
            ? 'rgba(0, 207, 243, 0.08)'
            : tokens.colors.primary[50],
        },
      },
      outlinedSecondary: {
        borderColor: mode === 'dark' ? tokens.colors.secondary.light : tokens.colors.secondary.darkText,
        borderWidth: '1.5px',
        color: mode === 'dark' ? tokens.colors.secondary.light : tokens.colors.secondary.darkText,
        '&:hover': {
          borderWidth: '1.5px',
          borderColor: mode === 'dark' ? tokens.colors.secondary.light : tokens.colors.secondary.darkText,
          backgroundColor: mode === 'dark'
            ? 'rgba(51, 235, 203, 0.08)'
            : 'rgba(0, 133, 110, 0.06)',
        },
      },
      textPrimary: {
        color: mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText,
        '&:hover': {
          backgroundColor: mode === 'dark'
            ? 'rgba(0, 207, 243, 0.08)'
            : tokens.colors.primary[50],
        },
      },
    },
  },
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
        border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : tokens.colors.neutral[200]}`,
        transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: mode === 'dark'
            ? '0 4px 6px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)'
            : '0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.03)',
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'medium',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: tokens.radius.lg,
          fontFamily: tokens.typography.fontFamily,
          transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-focused': {
            boxShadow: '0 0 20px rgba(0, 207, 243, 0.25)',
          },
          '&.Mui-error.Mui-focused': {
            boxShadow: '0 0 20px rgba(239, 68, 68, 0.25)',
          },
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.lg,
      },
      notchedOutline: {
        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : tokens.colors.neutral[300],
        transition: 'border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      },
    },
  },
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundColor: mode === 'dark' ? tokens.colors.neutral[950] : '#ffffff',
        borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : tokens.colors.neutral[200]}`,
        color: mode === 'dark' ? '#ffffff' : tokens.colors.neutral[900],
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.radius.xl,
        '@media (max-width: 599.95px)': {
          borderRadius: 0,
          margin: 0,
          maxHeight: '100%',
          maxWidth: '100%',
          width: '100%',
          height: '100%',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.pill,
        fontWeight: 700, // Lato has no weight 600; using 700
        fontFamily: tokens.typography.fontFamily,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.pill,
        textTransform: 'none' as const,
        fontWeight: 700, // Lato has no weight 600; using 700
        fontFamily: tokens.typography.fontFamily,
        minHeight: 40,
        padding: '8px 20px',
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&.Mui-selected': {
          background: tokens.gradients.primary,
          color: '#ffffff',
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        display: 'none',
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        background: tokens.gradients.primary,
        fontFamily: tokens.typography.fontFamily,
        fontWeight: 700,
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: tokens.radius.sm,
        fontFamily: tokens.typography.fontFamily,
        fontSize: '0.75rem',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
        fontFamily: tokens.typography.fontFamily,
      },
    },
  },
  MuiSkeleton: {
    defaultProps: {
      animation: 'wave',
    },
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.sm,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontFamily: tokens.typography.fontFamily,
        borderBottomColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : tokens.colors.neutral[200],
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: `0 ${tokens.radius.xl}px ${tokens.radius.xl}px 0`,
        width: 280,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: tokens.radius.lg,
        border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : tokens.colors.neutral[200]}`,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: tokens.radius.pill,
        height: 6,
      },
      bar: {
        borderRadius: tokens.radius.pill,
        background: tokens.gradients.horizontal,
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText,
        '&:hover': {
          color: mode === 'dark' ? tokens.colors.primary.main : tokens.colors.primary.dark,
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
        '&.Mui-focused': {
          color: mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText,
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
        '&.Mui-focused': {
          color: mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText,
        },
      },
    },
  },
});

// ─── Theme Factory ───────────────────────────────────────────────

export const createVoxistTheme = (mode: 'light' | 'dark') => {
  const baseTheme: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: tokens.colors.primary.main,
        light: tokens.colors.primary.light,
        // Intentional inversion in dark mode: light token used for dark slot
        // so that MUI text-on-primary uses a readable lighter shade.
        // In light mode, use darkText for WCAG AA 4.5:1+ contrast on white.
        dark: mode === 'dark' ? tokens.colors.primary.light : tokens.colors.primary.darkText,
        contrastText: tokens.colors.primary.contrastText,
      },
      secondary: {
        main: tokens.colors.secondary.main,
        light: tokens.colors.secondary.light,
        dark: tokens.colors.secondary.dark,
        contrastText: tokens.colors.secondary.contrastText,
      },
      error: {
        main: mode === 'dark' ? '#f87171' : tokens.colors.error.main,
        light: tokens.colors.error.light,
        dark: tokens.colors.error.dark,
      },
      warning: {
        main: mode === 'dark' ? '#fbbf24' : tokens.colors.warning.main,
        light: tokens.colors.warning.light,
        dark: tokens.colors.warning.dark,
      },
      success: {
        main: mode === 'dark' ? '#34d399' : tokens.colors.success.main,
        light: tokens.colors.success.light,
        dark: tokens.colors.success.dark,
      },
      info: {
        main: tokens.colors.primary.main,
        light: tokens.colors.primary[50],
        dark: tokens.colors.primary.dark,
      },
      background: {
        default: mode === 'dark' ? tokens.colors.neutral[950] : tokens.colors.neutral[100],
        paper: mode === 'dark' ? tokens.colors.neutral[800] : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#ffffff' : '#1a2233',
        secondary: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
        disabled: mode === 'dark' ? 'rgba(255, 255, 255, 0.38)' : '#94a3b8',
      },
      divider: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : tokens.colors.neutral[200],
      action: {
        hover: mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
        selected: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
      },
    },
    typography: {
      fontFamily: tokens.typography.fontFamily,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 400, // Lato has no weight 500/600; using 400 for medium
      fontWeightBold: 700,
      h1: {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.015em',
      },
      h2: {
        fontSize: '1.75rem',
        fontWeight: 700,
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.375rem',
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: 700, // Lato has no weight 600; using 700
        lineHeight: 1.35,
      },
      h5: {
        fontSize: '1rem',
        fontWeight: 700, // Lato has no weight 600; using 700
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '0.875rem',
        fontWeight: 700, // Lato has no weight 600; using 700
        lineHeight: 1.4,
        letterSpacing: '0.01em',
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.4,
        letterSpacing: '0.02em',
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 700,
        lineHeight: 1.6,
        letterSpacing: '0.08em',
        textTransform: 'uppercase' as const,
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: 1.6,
        letterSpacing: '0.04em',
        // Matches MuiButton.root override; CTA variants override to 'uppercase'
        textTransform: 'none' as const,
      },
    },
    shape: {
      borderRadius: tokens.radius.lg,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    transitions: {
      duration: {
        shortest: 100,
        shorter: 150,
        short: 200,
        standard: 300,
        complex: 500,
        enteringScreen: 300,
        leavingScreen: 200,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    custom: {
      gradients: tokens.gradients,
      radius: tokens.radius,
      glow: {
        primary: '0 0 20px rgba(0, 207, 243, 0.25)',
        primaryStrong: '0 0 30px rgba(0, 207, 243, 0.4)',
        tts: '0 0 20px rgba(0, 74, 87, 0.25)',
        error: '0 0 20px rgba(239, 68, 68, 0.25)',
        success: '0 0 20px rgba(16, 185, 129, 0.25)',
      },
      glass: {
        background: mode === 'dark' ? 'rgba(30, 30, 30, 0.7)' : 'rgba(255, 255, 255, 0.7)',
        border: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.2)',
        blur: 'blur(16px)',
      },
      fontFamilyMono: tokens.typography.fontFamilyMono,
    },
    components: getComponentOverrides(mode),
  };

  let theme = createTheme(baseTheme);
  theme = responsiveFontSizes(theme);
  return theme;
};

export { tokens };
export default createVoxistTheme;
